/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, InjectionToken, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ConfigKlaro, Translations } from '@core/types/cookies-consent.types';
import { NestedRecord } from '@core/types/utility.types';
import { resolveTranslation } from '@core/utils/translation-utilities';
import { TranslocoService } from '@jsverse/transloco';
import type { UserProfileCookieConsent } from '@kerberos-compliance/compliance-shared-ts/features/user/types/user-profile.types';
import * as Klaro from 'klaro/dist/klaro-no-translations-no-css';
import { ConsentManager } from 'klaro/dist/klaro-no-translations-no-css';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, exhaustMap, filter, map, tap } from 'rxjs/operators';
import { AuthStore } from '@/auth/store/auth.store';
import { UserProfileFacade } from '@/user/facades/user-profile.facade';

/**
 * The Klaro API the service uses, behind a token so specs can swap it via DI. Module-mocking
 * `klaro` with `vi.mock` is unreliable under `ng test`: the bundled spec does not always pick
 * up the mock, so the service would call the real library instead of the spies.
 */
export type KlaroApi = Pick<typeof Klaro, 'setup' | 'getManager' | 'show'>;

export const KLARO = new InjectionToken<KlaroApi>('KLARO', {
  providedIn: 'root',
  // A plain object, not the module namespace: the injector probes the value it gets back
  // (e.g. `constructor`), which throws on a `vi.mock`-ed namespace in other specs.
  factory: (): KlaroApi => ({ setup: Klaro.setup, getManager: Klaro.getManager, show: Klaro.show }),
});

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly klaro = inject(KLARO);

  private readonly translocoService = inject(TranslocoService);

  private readonly authStore = inject(AuthStore);

  private readonly userProfileFacade = inject(UserProfileFacade);

  private readonly document = inject(DOCUMENT);

  private klaroManager?: ConsentManager;

  private readonly klaroManagerIsNotYetInitialized = 'Klaro Manager is not yet initialized';

  private isInitialized = false;

  private klaroStylesLoaded = false;

  private readonly saveConsentSubject = new Subject<void>();

  public cookieTranslationObject = toSignal<NestedRecord>(this.translocoService.selectTranslateObject('cookieConsent'));

  public readonly consentChange = signal<{ serviceName: string; consent: boolean } | undefined>(undefined);

  private readonly destroyRef = inject(DestroyRef);

  public constructor() {
    this.saveConsentSubject
      .pipe(
        debounceTime(100),
        filter(() => this.isInitialized && this.authStore.isUserLoggedIn()),
        exhaustMap(() => this.performSaveToBackend()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * Initializes the necessary services and configuration for the application.
   *
   * This method performs the following actions:
   * 1. Loads the active language using the `translocoService`.
   * 2. Sets up the Klaro consent manager with the appropriate configuration.
   * 3. Assigns the Klaro manager instance to the `klaroManager` property.
   *
   * @returns {Observable<void>} An Observable that completes when initialization is done.
   */
  public initialize(): Observable<void> {
    return this.translocoService.load(this.translocoService.getActiveLang()).pipe(
      tap(() => {
        this.loadKlaroStyles();
        this.klaro.setup(this.getKlaroConfig());
        this.klaroManager = this.klaro.getManager();
        this.isInitialized = true;
      }),
      map(() => void 0),
    );
  }

  /**
   * Dynamically loads the Klaro stylesheet into the document's head if it has not already been loaded.
   * The method ensures the stylesheet is appended only once during the application lifecycle.
   */
  private loadKlaroStyles(): void {
    if (this.klaroStylesLoaded) {
      return;
    }
    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'klaro.css';
    this.document.head.append(link);
    this.klaroStylesLoaded = true;
  }

  /**
   * Shows the cookie consent banner if the user hasn't already consented to all required cookies.
   * This method should be called after the service is initialized.
   *
   * @returns {Observable<void>} An Observable that completes when the banner check is done.
   */
  public showConsentBannerIfNeeded(): Observable<void> {
    if (this.klaroManager) {
      this.checkAndShowBanner();
      return of(void 0);
    }
    return this.translocoService.load(this.translocoService.getActiveLang()).pipe(
      tap(() => {
        this.klaro.setup(this.getKlaroConfig());
        this.klaroManager = this.klaro.getManager();
        this.checkAndShowBanner();
      }),
      map(() => void 0),
    );
  }

  /**
   * Shows the settings dialog when the user has no cookie consent saved on their
   * user profile. The decision is driven by the persisted profile preference
   * (`userPreferences.cookieConsent`) rather than Klaro's local cookie, so a
   * user without saved preferences is always prompted after login — even on a
   * browser that happens to carry a stale Klaro consent cookie.
   *
   * Note: The intermediate banner was removed - we now show the full settings dialog directly.
   */
  private checkAndShowBanner(): void {
    const requiredServices: (keyof UserProfileCookieConsent)[] = ['jotform'];
    const savedConsent = this.userProfileFacade.userProfile()?.userPreferences?.cookieConsent;
    const isNeedsConsent = requiredServices.some((service) => savedConsent?.[service] === undefined);

    if (isNeedsConsent) {
      this.showSettingsDialog();
    }
  }

  /**
   * Displays the settings dialog for the user, allowing users to adjust their preferences.
   */
  public showSettingsDialog(): void {
    if (this.klaroManager) {
      this.klaro.show(this.getKlaroConfig(), true);
    } else {
      this.klaro.setup(this.getKlaroConfig());
      this.klaroManager = this.klaro.getManager();
      this.klaro.show(this.getKlaroConfig(), true);
    }
  }

  private ensureKlaroManager(): ConsentManager {
    if (!this.klaroManager) {
      this.loadKlaroStyles();
      this.klaro.setup(this.getKlaroConfig());
      this.klaroManager = this.klaro.getManager();
      this.isInitialized = true;
    }
    if (!this.klaroManager) {
      throw new Error(this.klaroManagerIsNotYetInitialized);
    }
    return this.klaroManager;
  }

  /**
   * Updates the consent status for a specified service and saves the updated consent.
   * Note: Backend persistence is handled by the Klaro callback, not here.
   *
   * @param {string} serviceName The name of the service for which the consent status is being updated.
   * @param {boolean} consent A boolean value representing the new consent status; true for consent given, false for consent denied.
   * @throws {Error} Error if the Klaro manager has not been initialized.
   */
  public updateAndSaveConsent(serviceName: string, consent: boolean): void {
    const manager = this.ensureKlaroManager();
    manager.updateConsent(serviceName, consent);
    manager.saveAndApplyConsents('save');
    this.consentChange.set({ serviceName, consent });
  }

  /**
   * Retrieves the saved consent status for a specified service.
   *
   * @param {string} serviceName - The name of the service for which to get the saved consent status.
   * @returns {boolean} The saved consent status of the specified service.
   * @throws {Error} Will throw an error if the klaroManager is not initialized.
   */
  public getSavedConsent(serviceName: string): boolean {
    const manager = this.ensureKlaroManager();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return manager.savedConsents[serviceName];
  }

  /**
   * Retrieves a service by its name from the Klaro manager.
   *
   * @param {string} serviceName - The name of the service to retrieve.
   * @returns {any} The requested service.
   * @throws {Error} If the Klaro manager is not initialized.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getService(serviceName: string): any {
    const manager = this.ensureKlaroManager();
    return manager.getService(serviceName);
  }

  /**
   * Retrieves all saved cookie consents from the Klaro manager.
   *
   * @returns {UserProfileCookieConsent} An object containing the consent status for each service.
   * @throws {Error} If the Klaro manager is not initialized.
   */
  public getAllSavedConsents(): UserProfileCookieConsent {
    const manager = this.ensureKlaroManager();
    const savedConsents = manager.savedConsents as Record<string, boolean>;
    return {
      jotform: savedConsents['jotform'],
    };
  }

  /**
   * Saves the current cookie consent preferences to the user profile backend.
   * Consent is stored inside userPreferences.cookieConsent.
   * Uses debouncing to prevent multiple rapid calls (Klaro triggers callback per service).
   */
  public saveConsentToBackend(): void {
    this.saveConsentSubject.next();
  }

  private performSaveToBackend(): Observable<void> {
    const cookieConsent = this.getAllSavedConsents();

    if (cookieConsent.jotform === undefined) {
      return of(void 0);
    }

    const savedConsent = this.userProfileFacade.userProfile()?.userPreferences?.cookieConsent;
    if (savedConsent?.jotform === cookieConsent.jotform) {
      return of(void 0);
    }

    return from(this.userProfileFacade.updateUserPreferences({ cookieConsent })).pipe(
      map(() => void 0),
      catchError(() => of(void 0)),
    );
  }

  /**
   * Applies saved cookie consent from user profile to the Klaro manager.
   * This should be called after user login to restore their consent preferences.
   *
   * @param {UserProfileCookieConsent | undefined} cookieConsent - The cookie consent from user profile.
   */
  public applySavedConsent(cookieConsent: UserProfileCookieConsent | undefined): void {
    if (!cookieConsent || !this.klaroManager) {
      return;
    }

    if (cookieConsent.jotform !== undefined) {
      this.klaroManager.updateConsent('jotform', cookieConsent.jotform);
    }
    this.klaroManager.saveAndApplyConsents('save');
  }

  /**
   * Generates and returns the Klaro configuration object.
   * The configuration object controls the behavior and appearance of the Klaro consent management tool.
   *
   * @returns {ConfigKlaro} The Klaro configuration object.
   */
  private getKlaroConfig(): ConfigKlaro {
    return {
      version: 1,
      elementID: 'klaro',
      styling: {
        theme: ['dark', 'bottom', 'right'],
      },
      noAutoLoad: true,
      htmlTexts: true,
      embedded: false,
      groupByPurpose: false,
      storageMethod: 'cookie',
      cookieExpiresAfterDays: 180,
      default: false,
      mustConsent: false,
      acceptAll: true,
      hideDeclineAll: true,
      hideLearnMore: false,
      noticeAsModal: true,
      lang: this.translocoService.getActiveLang(),
      translations: {
        [this.translocoService.getActiveLang()]: this.getTranslations(),
      },
      services: [
        {
          name: 'jotform',
          contextualConsentOnly: false,
          purposes: ['functional-provide-inputs'],
        },
      ],
      callback: (consent, service): void => {
        if (!this.klaroManager) {
          return;
        }
        this.saveConsentToBackend();
        if (service?.name) {
          this.consentChange.set({ serviceName: service.name, consent });
        }
      },
    };
  }

  /**
   * Retrieves the translations required for the cookie consent modal and notice components.
   * This method constructs an object containing various translated strings for each component and purpose.
   *
   * @returns {Translations} - An object containing all the translated strings.
   */

  private getTranslations(): Translations {
    return {
      acceptAll: resolveTranslation(this.cookieTranslationObject()!, ['modal', 'acceptAll']),
      disableAll: {
        title: 'Close',
        description: 'description',
      },
      acceptSelected: resolveTranslation(this.cookieTranslationObject()!, ['modal', 'acceptSelected']),
      consentModal: {
        title: resolveTranslation(this.cookieTranslationObject()!, ['modal', 'header']),
        description: resolveTranslation(this.cookieTranslationObject()!, ['modal', 'text']),
      },
      consentNotice: {
        description: resolveTranslation(this.cookieTranslationObject()!, ['notice', 'text']),
        learnMore: resolveTranslation(this.cookieTranslationObject()!, ['notice', 'learnMore']),
        changeDescription: resolveTranslation(this.cookieTranslationObject()!, ['notice', 'updated-conditions']),
      },
      jotform: {
        description: resolveTranslation(this.cookieTranslationObject()!, ['modal', 'descriptions', 'jotform']),
      },
      purposes: {
        'functional-provide-inputs': {
          title: resolveTranslation(this.cookieTranslationObject()!, [
            'modal',
            'purposes',
            'functional-provide-inputs',
          ]),
        },
      },
      ok: resolveTranslation(this.cookieTranslationObject()!, ['notice', 'accept']),
      poweredBy: resolveTranslation(this.cookieTranslationObject()!, ['poweredBy']),
      service: {
        purpose: resolveTranslation(this.cookieTranslationObject()!, ['modal', 'purpose']),
        disableAll: {
          title: resolveTranslation(this.cookieTranslationObject()!, ['modal', 'disableAll', 'title']),
          description: resolveTranslation(this.cookieTranslationObject()!, ['modal', 'disableAll', 'description']),
        },
      },
      save: resolveTranslation(this.cookieTranslationObject()!, ['save']),
      close: resolveTranslation(this.cookieTranslationObject()!, ['close']),
    };
  }
}
